import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  quoteCivilOf,
  resolveQuoteReceiptCivilTime,
  type QuoteReceiptPrefill,
} from './quoteReviewContract';

interface QuoteReceiptTimeFieldProps {
  /** The orchestrator's pre-fill: an instant, or null with the reason. Never evidence by itself. */
  prefill: QuoteReceiptPrefill;
  /** The reviewed quote's workshop timezone; the inputs are civil time in it. */
  timeZone: string;
  /** The staff-confirmed instant (ISO with offset), or null while it is not valid. */
  onChange: (receivedAt: string | null) => void;
  /** Injectable clock for the not-in-the-future check. */
  now?: () => number;
  /** The time a refused confirm carried (#2821): restored instead of the pre-fill. */
  initialReceivedAt?: string | undefined;
  /** Why the server refused that confirm; shown until the time is changed. */
  serverError?:
    | 'quote_receipt_time_required'
    | 'quote_receipt_time_invalid'
    | 'quote_receipt_time_future'
    | undefined;
  /**
   * RECONCILING (#2821): an attempt may already exist and the server re-sends
   * its pinned time (`initialReceivedAt`) whatever is entered, so the value
   * is shown read-only and reported as is; nothing on the card can change it.
   */
  locked?: boolean;
}

/**
 * The staff member CONFIRMS when the customer approval was received (#2821):
 * a required date + time in the WORKSHOP timezone, pre-filled only when the
 * orchestrator found exactly one candidate in the acceptance sentence. A
 * skipped DST time is an inline error; a repeated one asks which occurrence;
 * a future time is an inline error. Only a valid value reaches `onChange`.
 */
export function QuoteReceiptTimeField({
  prefill,
  timeZone,
  onChange,
  now = Date.now,
  initialReceivedAt,
  serverError,
  locked = false,
}: QuoteReceiptTimeFieldProps) {
  const { t } = useTranslation('assistant');
  const start = initialReceivedAt ?? prefill.receivedAt ?? undefined;
  const initial = useMemo(
    () => (start ? quoteCivilOf(start, timeZone) : undefined),
    [start, timeZone]
  );
  const [date, setDate] = useState(initial?.date ?? '');
  const [time, setTime] = useState(initial?.time ?? '');
  // A starting instant is exact, so it also selects a repeated hour's occurrence.
  const [chosen, setChosen] = useState<string | undefined>(start);

  // Locked: what is SHOWN and what is SUBMITTED both come from the current
  // pin, re-derived on every render, so a pin that changes after mount (a
  // delayed socket event re-created this card, then a 503 named a different
  // pin) can never be displayed one way and sent another. Entered state is
  // ignored while locked.
  const pinCivil = locked && initialReceivedAt ? quoteCivilOf(initialReceivedAt, timeZone) : undefined;
  const shownDate = locked ? (pinCivil?.date ?? '') : date;
  const shownTime = locked ? (pinCivil?.time ?? '') : time;
  const shownChoice = locked ? initialReceivedAt : chosen;
  const at = now();
  const resolution = resolveQuoteReceiptCivilTime(shownDate, shownTime, timeZone, at, shownChoice);
  // The occurrences of a repeated hour stay on screen once one is chosen.
  const fold = resolveQuoteReceiptCivilTime(shownDate, shownTime, timeZone, at);
  const occurrences = fold.kind === 'repeated' ? fold.options : [];
  const selected = occurrences.find(
    (option) =>
      shownChoice !== undefined && Date.parse(option.receivedAt) === Date.parse(shownChoice)
  );
  // Locked: the server's pinned time, exactly; this device's clock and
  // inputs do not get a say (a browser behind the server must not block it).
  const confirmed = locked
    ? (initialReceivedAt ?? null)
    : resolution.kind === 'ok'
      ? resolution.receivedAt
      : null;
  useEffect(() => {
    onChange(confirmed);
  }, [confirmed, onChange]);

  const unchanged = initial !== undefined && date === initial.date && time === initial.time;
  const untouched = initialReceivedAt === undefined && unchanged && confirmed !== null;
  const error =
    locked || resolution.kind === 'ok' || resolution.kind === 'incomplete'
      ? undefined
      : resolution.kind;

  return (
    <Box data-testid="quote-receipt-time-field" sx={{ my: 1 }}>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {t('quoteReview.receipt.title')}
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
        <TextField
          size="small"
          type="date"
          required
          label={t('quoteReview.receipt.date')}
          value={shownDate}
          onChange={(event) => setDate(event.target.value)}
          error={error !== undefined && error !== 'repeated'}
          slotProps={{
            inputLabel: { shrink: true },
            htmlInput: { 'data-testid': 'quote-receipt-date', readOnly: locked },
          }}
        />
        <TextField
          size="small"
          type="time"
          required
          label={t('quoteReview.receipt.time')}
          value={shownTime}
          onChange={(event) => setTime(event.target.value)}
          error={error !== undefined && error !== 'repeated'}
          slotProps={{
            inputLabel: { shrink: true },
            htmlInput: { 'data-testid': 'quote-receipt-time', readOnly: locked },
          }}
        />
      </Box>
      {occurrences.length > 0 && (
        <FormControl required sx={{ mt: 1 }}>
          <FormLabel id="quote-receipt-offset-label">{t('quoteReview.receipt.offset')}</FormLabel>
          <RadioGroup
            row
            aria-labelledby="quote-receipt-offset-label"
            value={selected?.receivedAt ?? ''}
            onChange={(event) => setChosen(event.target.value)}
          >
            {occurrences.map((option) => (
              <FormControlLabel
                key={option.receivedAt}
                value={option.receivedAt}
                control={<Radio size="small" disabled={locked} />}
                label={t('quoteReview.receipt.offsetOption', {
                  time: shownTime,
                  offset: option.offset,
                })}
              />
            ))}
          </RadioGroup>
        </FormControl>
      )}
      <Typography variant="caption" color="text.secondary" component="p">
        {t('quoteReview.receipt.timezone', { timezone: timeZone })}
      </Typography>
      {error !== undefined && (
        <Typography variant="caption" color="error" component="p" role="alert">
          {t(`quoteReview.receipt.errors.${error}`)}
        </Typography>
      )}
      {locked && (
        <Typography
          variant="caption"
          color="warning.main"
          component="p"
          data-testid="quote-receipt-locked"
        >
          {t('quoteReview.receipt.locked')}
        </Typography>
      )}
      {serverError !== undefined &&
        (unchanged || (initial === undefined && date === '' && time === '')) && (
        <Typography
          variant="caption"
          color="error"
          component="p"
          data-testid="quote-receipt-server-error"
        >
          {t(`quoteReview.receipt.server.${serverError}`)}
        </Typography>
      )}
      {untouched && (
        <Typography variant="caption" color="text.secondary" component="p">
          {t('quoteReview.receipt.prefilled')}
        </Typography>
      )}
      {prefill.status !== 'ok' && date === '' && time === '' && (
        <Typography variant="caption" color="text.secondary" component="p">
          {t(`quoteReview.receipt.status.${prefill.status}`)}
        </Typography>
      )}
      {confirmed === null && (
        <Typography variant="caption" color="warning.main" component="p">
          {t('quoteReview.receipt.required')}
        </Typography>
      )}
    </Box>
  );
}
